/* Pureverr FilterPro product page. NEW FILE, 2026-09-17, built in isolation for the V7 line.
   Loaded (deferred) only by sections/pureverr-filterpro-main.liquid. No library, no network request, no cart call:
   adding to the cart is the theme's own product-form.js and nothing here touches the form, its inputs or its submit.

   1. Gallery: the list is a native scroll-snap scroller, so swipe, trackpad and keyboard scrolling work without this
      file. Script adds: previous / next buttons, thumbnails, arrow keys on the focused list, and the "2 / 5" readout.
      The current slide is read from the scroll position, never stored, so every input stays in agreement.
   2. Lazy slides: a lazy image inside a horizontal scroller may not start loading until it is scrolled to (measured on
      the V7 product deck, 2026-09-14), which shows an empty card mid-swipe. Once the first image has loaded, the other
      slides are switched to eager with low priority.
   3. Sticky bar: shown whenever the main Add to cart button is outside the viewport (so a phone, where the button sits
      below the first screen, has an Add to cart from the start), hidden near the footer, while the cart drawer is open,
      and in the Theme Editor. It is a second submit button of the same form; this file never submits anything itself.
      While the bar is up the page gets a scroll-padding-bottom so keyboard focus is never covered by it.
   Theme Editor: re-initialises on shopify:section:load and scrolls to a slide on shopify:block:select. */
(function () {
  'use strict';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function initGallery(root) {
    var list = root.querySelector('[data-pv-fp-list]');
    if (!list || list.dataset.pvFpReady) return;
    var slides = Array.prototype.slice.call(list.querySelectorAll('[data-pv-fp-slide]'));
    if (slides.length < 2) return;
    list.dataset.pvFpReady = '1';
    // the grab cursor is applied only once a drag is really wired, so the affordance cannot outlive the behaviour
    list.classList.add('is-draggable');
    /* 2026-09-19 (UI-polish brief I): the arrow buttons and the "1 / 4" readout are gone; the control is the shared
       dot row, which assets/pureverr-pagecontrol.js drives. `readout` is now the visually-hidden live region that keeps
       the SPOKEN "Image 2 of 4" the visible counter used to carry. */
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-pv-fp-dot]'));
    var readout = root.querySelector('[data-pv-fp-current]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-pv-fp-thumb]'));
    var current = -1;
    var ticking = false;

    function indexNow() {
      var origin = list.getBoundingClientRect().left + (parseFloat(getComputedStyle(list).scrollPaddingLeft) || 0);
      var best = 0, bestDist = Infinity;
      slides.forEach(function (s, i) {
        var d = Math.abs(s.getBoundingClientRect().left - origin);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      return best;
    }
    function paint() {
      ticking = false;
      var i = indexNow();
      if (i === current) return;
      current = i;
      if (readout) readout.textContent = String(i + 1);
      /* KEEPING THE DOTS IN STEP IS THIS FILE'S JOB. The shared control marks dots itself only DURING a scrub (where
         waiting on a scroll sync would lag the finger); every other way of moving the deck - a swipe, a mouse drag, the
         arrow keys, a thumbnail, a tap-step - lands here, exactly as the base theme's slider JS does it on the PDP. */
      dots.forEach(function (d, n) {
        var on = n === i;
        d.classList.toggle('slider-counter__link--active', on);
        if (on) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
      thumbs.forEach(function (t, n) {
        if (n === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
    }
    function go(i) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      var origin = list.getBoundingClientRect().left + (parseFloat(getComputedStyle(list).scrollPaddingLeft) || 0);
      var delta = slides[i].getBoundingClientRect().left - origin;
      list.scrollBy({ left: delta, behavior: reduce && reduce.matches ? 'instant' : 'smooth' });
    }
    list.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(paint); }
    }, { passive: true });
    /* a plain click on a dot works with the shared control absent; the control adds tap-step, scrub and arrow keys on
       top and stops the clicks it has already acted on in the capture phase */
    dots.forEach(function (d) {
      d.addEventListener('click', function () { go(parseInt(d.getAttribute('data-pv-fp-dot'), 10) || 0); });
    });
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { go(parseInt(t.getAttribute('data-pv-fp-thumb'), 10) || 0); });
    });
    list.addEventListener('keydown', function (e) {
      if (e.target !== list) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); go(indexNow() + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(indexNow() - 1); }
      else if (e.key === 'Home') { e.preventDefault(); go(0); }
      else if (e.key === 'End') { e.preventDefault(); go(slides.length - 1); }
    });
    window.addEventListener('resize', function () { current = -1; paint(); }, { passive: true });

    /* ---- THE SHARED PAGE CONTROL (2026-09-19, UI-polish brief I) -------------------------------------------------
       The same object the FaucetPro PDP uses, handed this gallery's own selectors. It is not a copy and not an
       approximation: assets/pureverr-pagecontrol.js is the one implementation and this is its second caller.
       Guarded on both sides - if that file is missing the dots above are still real buttons wired to `go()`. */
    if (window.PureverrPageControl) {
      window.PureverrPageControl.init(root, {
        bar: '[data-pv-fp-dots]',
        list: '[data-pv-fp-list]',
        slide: '[data-pv-fp-slide]'
      });
    }

    /* MOUSE DRAGGING (2026-09-19, owner-override brief F.4: "mouse dragging ... appropriate grab/grabbing cursor ...
       momentum or snapping consistent with the existing PDP").
       A pointer drag is turned into a scroll of this same list, so every input - finger, trackpad, mouse, arrow keys,
       arrow buttons, thumbnails - moves ONE scroll position and the native scroll-snap decides where it settles. There is
       no second state to keep in sync and nothing to drift.
       TOUCH IS NOT TOUCHED: `pointerType === 'touch'` returns immediately, so a phone keeps the browser's own fling
       physics and its own snap. While a mouse drag runs, `is-dragging` suspends snap (a mandatory snap fights a drag),
       turns the cursor to `grabbing`, suspends text selection and takes the images out of the hit test; on release the
       deck is scrolled to the nearest slide with the shared `go()` - the same easing the arrows use, instant under
       reduced motion - and snap is restored once that scroll has settled, so it cannot yank the deck mid-animation.
       The click that ends a drag is swallowed in the capture phase, so a drag never activates anything underneath. */
    var drag = null, dragMoved = false, snapBackTimer = 0;
    var DRAG_START_PX = 4;
    function endDrag(e) {
      if (!drag || (e && e.pointerId !== drag.id)) return;
      var moved = drag.moved;
      try { list.releasePointerCapture(drag.id); } catch (err) {}
      drag = null;
      if (!moved) { list.classList.remove('is-dragging'); return; }
      dragMoved = true;
      window.setTimeout(function () { dragMoved = false; }, 350);
      go(indexNow());
      // snap comes back only once the settling scroll is done, so restoring it cannot jump the deck mid-animation
      clearTimeout(snapBackTimer);
      snapBackTimer = window.setTimeout(function () { list.classList.remove('is-dragging'); }, 360);
    }
    list.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest && e.target.closest('a, button, input, select, textarea, summary')) return;
      drag = { id: e.pointerId, x0: e.clientX, left0: list.scrollLeft, moved: false };
      try { list.setPointerCapture(e.pointerId); } catch (err) {}
    });
    list.addEventListener('pointermove', function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.x0;
      if (!drag.moved) {
        if (Math.abs(dx) < DRAG_START_PX) return;
        drag.moved = true;
        clearTimeout(snapBackTimer);
        list.classList.add('is-dragging');
      }
      e.preventDefault();
      list.scrollLeft = drag.left0 - dx;
    });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(function (t) { list.addEventListener(t, endDrag); });
    list.addEventListener('click', function (e) {
      if (!dragMoved) return;
      dragMoved = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);
    list.addEventListener('dragstart', function (e) { e.preventDefault(); });

    paint();

    // lazy slides -> eager, once the first image is in
    var imgs = Array.prototype.slice.call(list.querySelectorAll('img'));
    function wake() {
      imgs.slice(1).forEach(function (img) {
        if (img.loading === 'lazy') { img.setAttribute('fetchpriority', 'low'); img.loading = 'eager'; }
      });
    }
    if (imgs[0]) {
      if (imgs[0].complete) wake(); else imgs[0].addEventListener('load', wake, { once: true });
    }

    root._pvFpGo = go;
  }

  function initSticky(root) {
    var bar = root.querySelector('[data-pv-fp-sticky]');
    var purchase = root.querySelector('[data-pv-fp-purchase]');
    if (!bar || !purchase || bar.dataset.pvFpReady) return;
    if (window.Shopify && window.Shopify.designMode) return;
    bar.dataset.pvFpReady = '1';
    var main = purchase.querySelector('[type="submit"]') || purchase;
    var stickyBtn = bar.querySelector('button');
    var footer = document.querySelector('.shopify-section-group-footer-group, footer, .footer');
    var docEl = document.documentElement;
    var want = false, hideTimer = null, ticking = false, wasOpen = false, pressedSticky = false;

    // Only `active` means open. The theme's cart-drawer.js adds `animate` AND `active` on open and removes only `active` on
    // close, so `animate` stays for the rest of the page view; treating it as "open" hid this bar for good after the first
    // add (independent review M1, measured with V7's own drawer script).
    function drawerOpen() {
      var d = document.querySelector('cart-drawer');
      return !!(d && d.classList.contains('active'));
    }
    // While the bar is up, keyboard focus must not land underneath it (WCAG 2.2 SC 2.4.11; independent review M3 measured a
    // fully covered FAQ button at 390px). scroll-padding-bottom makes the browser's own focus scrolling stop above the bar.
    // It is the one thing this file sets outside its root, only on this page, and it is removed with the bar.
    function pad(on) {
      if (on) docEl.style.scrollPaddingBottom = (bar.offsetHeight + 12) + 'px';
      else docEl.style.removeProperty('scroll-padding-bottom');
    }
    // State is read from live geometry on every pass. An IntersectionObserver only reports CHANGES, so a jump from the
    // footer to the top (the theme's scroll-to-top button, an anchor) left a cached "scrolled past" state behind and the bar
    // stayed up over the hero (measured in local QA, 2026-09-18).
    function update() {
      ticking = false;
      var vh = window.innerHeight || docEl.clientHeight;
      var r = main.getBoundingClientRect();
      var mainInView = r.bottom > 0 && r.top < vh;
      var nearEnd = footer ? footer.getBoundingClientRect().top < vh : false;
      var open = drawerOpen();
      var show = !mainInView && !nearEnd && !open;
      // The drawer hands focus back to the element that opened it. After an add from the sticky button that element is
      // still hidden at that moment, so focus fell to <body> (review L1). Send it to a real Add to cart instead.
      if (wasOpen && !open && pressedSticky) {
        pressedSticky = false;
        window.setTimeout(function () {
          var ae = document.activeElement;
          if (ae && ae !== document.body) return;
          var target = (want && !bar.hidden) ? stickyBtn : main;
          if (target && target.focus) target.focus({ preventScroll: target === stickyBtn });
        }, 320);
      }
      wasOpen = open;
      if (show === want) return;
      want = show;
      if (show) {
        if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
        bar.hidden = false;
        pad(true);
        // next frame, so the slide-in runs from the hidden position; re-check, the state may have flipped back meanwhile
        window.requestAnimationFrame(function () { if (want) bar.classList.add('is-shown'); });
      } else {
        bar.classList.remove('is-shown');
        pad(false);
        hideTimer = setTimeout(function () { if (!want) bar.hidden = true; hideTimer = null; }, 260);
      }
    }
    function queue() { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    if (stickyBtn) stickyBtn.addEventListener('click', function () { pressedSticky = true; });
    var drawer = document.querySelector('cart-drawer');
    if (drawer && 'MutationObserver' in window) {
      new MutationObserver(queue).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
    // scroll-padding only acts when the browser has to scroll a focused element INTO view. One that is already inside the
    // viewport but under the bar stays covered (measured at 1440: an inline link 19px under the bar), so keyboard focus that
    // lands under the bar is nudged up. Keyboard focus only (:focus-visible); a pointer cannot reach what the bar covers.
    document.addEventListener('focusin', function (e) {
      var el = e.target;
      if (!want || bar.hidden || !el || !el.getBoundingClientRect || bar.contains(el)) return;
      try { if (el.matches && !el.matches(':focus-visible')) return; } catch (x) { /* older engines: treat as keyboard focus */ }
      var over = el.getBoundingClientRect().bottom - (bar.getBoundingClientRect().top - 12);
      if (over > 0) window.scrollBy({ top: over, behavior: (reduce && reduce.matches) ? 'instant' : 'smooth' });
    });
    // An add error is written beside the MAIN button (product-form.js), which is off screen whenever this bar was the button
    // that was pressed (review L6). Bring the message into view when it appears.
    var err = root.querySelector('.product-form__error-message-wrapper');
    if (err && 'MutationObserver' in window) {
      new MutationObserver(function () {
        if (!err.hidden) purchase.scrollIntoView({ block: 'center', behavior: (reduce && reduce.matches) ? 'auto' : 'smooth' });
      }).observe(err, { attributes: true, attributeFilter: ['hidden'] });
    }
    queue();
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-pv-fp-main]');
    Array.prototype.forEach.call(roots, function (root) { initGallery(root); initSticky(root); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var slide = e.target && e.target.closest && e.target.closest('[data-pv-fp-slide]');
    if (!slide) return;
    var root = slide.closest('[data-pv-fp-main]');
    var list = slide.parentNode;
    if (root && root._pvFpGo) root._pvFpGo(Array.prototype.indexOf.call(list.children, slide));
  });
})();
